import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Accounting from "accounting";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import AutoBillingBadge from "../../Components/AutoBillingBadge";
import * as api from "../../Service/api";
import { plansFromPayload, readLinkedPlansCache, planIdOf } from "./linkedPlanUtils";

const formatPlanCost = (plan) => {
  if (plan?.cost == null || plan?.cost === "") return null;
  const amount = Number(plan.cost);
  if (Number.isNaN(amount)) return null;
  const currency = plan.business?.currency;
  if (!currency) {
    return Accounting.formatMoney(amount, { precision: 2 });
  }
  return Accounting.formatMoney(amount, {
    symbol: currency,
    format: "%s %v",
    thousand: ",",
    decimal: ".",
    precision: 2,
  });
};

const getPlanDetailsLabel = (plan) => {
  const parts = [];
  if (plan?.name) parts.push(plan.name);
  if (plan?.billingPeriod) parts.push(plan.billingPeriod);
  const costLabel = formatPlanCost(plan);
  if (costLabel) parts.push(costLabel);
  return parts.join(" - ");
};

const sortPlansByName = (plans) =>
  [...plans].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

const toArray = (data) => plansFromPayload(data);

function LinkPlanBusinessCustomerModal({
  visible,
  onHide,
  customerId,
  customerName,
  customerPhoneNo,
  recentSubPlan,
  planGroups,
  fetchingPlans,
  selectedPlanIds,
  onPlanChange,
  onSubmit,
  linking,
  submitAttempted,
  submitError,
}) {
  const { t } = useTranslation("common");
  const selectedIds = (Array.isArray(selectedPlanIds) ? selectedPlanIds : [selectedPlanIds])
    .map((id) => (id == null ? "" : String(id)))
    .filter(Boolean);
  const planInvalid = submitAttempted && !selectedIds.length;
  const [linkedPlanIds, setLinkedPlanIds] = useState(() => new Set());
  const [fetchingLinkedPlans, setFetchingLinkedPlans] = useState(false);

  useEffect(() => {
    if (!visible || !customerId) {
      setLinkedPlanIds(new Set());
      setFetchingLinkedPlans(false);
      return undefined;
    }
    let cancelled = false;
    setFetchingLinkedPlans(true);
    api.getBusinessCustomerLinkedPlans(customerId)
      .then((response) => {
        if (cancelled) return;
        const plans = response.ok ? toArray(response.data) : [];
        const ids = new Set(
          [...plans, ...readLinkedPlansCache(customerId)]
            .map((plan) => planIdOf(plan))
            .filter(Boolean)
        );
        setLinkedPlanIds(ids);
      })
      .catch(() => {
        if (!cancelled) setLinkedPlanIds(new Set());
      })
      .finally(() => {
        if (!cancelled) setFetchingLinkedPlans(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, customerId]);

  const groupedPlanOptions = useMemo(
    () =>
      [...planGroups]
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .map((group) => ({
          label: group.name,
          items: sortPlansByName(group.plans || [])
            .filter((plan) => !linkedPlanIds.has(String(plan.id)))
            .map((plan) => ({
              ...plan,
              label: getPlanDetailsLabel(plan) || plan.name || "",
            })),
        }))
        .filter((group) => group.items.length > 0),
    [planGroups, linkedPlanIds]
  );

  const selectedPlans = useMemo(() => {
    if (!selectedIds.length) return [];
    const idSet = new Set(selectedIds);
    const found = [];
    for (const group of groupedPlanOptions) {
      for (const item of group.items || []) {
        if (idSet.has(String(item.id))) found.push(item);
      }
    }
    return found;
  }, [groupedPlanOptions, selectedIds]);

  const hasPlans = groupedPlanOptions.some((group) => group.items?.length);

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={t("common.cancel")}
        className="p-button-outlined"
        onClick={onHide}
        disabled={linking}
      />
      <Button
        label={
          linking ? t("business_customers.linking_plan") : t("business_customers.link_plan")
        }
        className="link-customer-modal__link-btn"
        onClick={onSubmit}
        disabled={linking || fetchingPlans || fetchingLinkedPlans || !hasPlans}
        loading={linking}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={t("business_customers.link_plan_title")}
      style={{ width: "520px", maxWidth: "95vw" }}
      contentStyle={{ padding: "1.5rem 1.75rem" }}
      footer={footer}
      modal
      dismissableMask={!linking}
      closable={!linking}
      className="link-plan-modal"
    >
      {(customerName || customerPhoneNo) && (
        <div className="mb-3 text-color-secondary">
          {customerName && customerName !== null && (
            <div>
              {t("business_customers.subscriber_name")}:{" "}
              <strong className="text-color">{customerName}</strong>
            </div>
          )}
          {customerPhoneNo && customerPhoneNo !== null && (
            <div>
              {t("common.number")}:{" "}
              <strong className="text-color">{customerPhoneNo}</strong>
            </div>
          )}
          {recentSubPlan && (
            <div className="mt-1">
              {t("business_customers.recent_subscription")}:{" "}
              <strong className="text-color">{recentSubPlan}</strong>
            </div>
          )}
        </div>
      )}

      <div className="field">
        <label htmlFor="link-plan-select" className="block mb-2">
          {t("business_customers.choose_plan")}
        </label>
        <MultiSelect
          id="link-plan-select"
          value={selectedPlans}
          options={groupedPlanOptions}
          optionLabel="label"
          dataKey="id"
          optionGroupLabel="label"
          optionGroupChildren="items"
          placeholder={t("reportConfigs.filter_plans_placeholder")}
          filter
          display="chip"
          itemTemplate={(plan) => (
            <span className="flex align-items-center gap-2">
              <span>{plan.label}</span>
              <AutoBillingBadge collectionMode={plan.collectionMode} />
            </span>
          )}
          selectedItemTemplate={(plan) => plan ? (
            <span className="flex align-items-center gap-2">
              <span>{plan.label}</span>
              <AutoBillingBadge collectionMode={plan.collectionMode} />
            </span>
          ) : null}
          className={`w-full link-plan-multiselect ${planInvalid ? "p-invalid" : ""}`}
          panelClassName="link-plan-multiselect-panel"
          disabled={fetchingPlans || fetchingLinkedPlans || linking || !hasPlans}
          loading={fetchingPlans || fetchingLinkedPlans}
          onChange={(e) => {
            const next = e.value || [];
            onPlanChange(next.map((plan) => String(plan.id)).filter(Boolean));
          }}
        />
        {planInvalid && (
          <small className="p-error block mt-1">
            {t("business_customers.plan_link_required")}
          </small>
        )}
        {!fetchingPlans && !fetchingLinkedPlans && !hasPlans && (
          <small className="text-color-secondary block mt-1">
            {t("business_customers.no_plans_to_link")}
          </small>
        )}
      </div>

      {submitError && (
        <Message
          severity="error"
          text={t("business_customers.plan_link_error")}
          className="w-full mt-3"
        />
      )}
    </Dialog>
  );
}

export default LinkPlanBusinessCustomerModal;
